"use client";

// Visual for the "Get served Intelligent performance insights" bento card
// (design handoff: insight-stream-animation.md): Intelligence notifications
// land one after another on a stack, each revealing What / Why / Next. A fixed
// 540x210 stage on a 13s rAF loop, scaled down to fit narrower cards. Below
// 536px the stage is the card width at full size (taller, so text wraps and
// stays readable) and flows under the card text.

import * as React from "react";

import { cn } from "@/lib/utils";

import { getInsightFrame, INSIGHT_REST_SECONDS } from "./insight-timeline";
import { useLoopSeconds, useStageScale } from "./use-stage-loop";

export interface InsightCardCopy {
  title: string;
  tag: string;
  what: string;
  why: string;
  next: string;
}

interface InsightVisualProps {
  cards: readonly InsightCardCopy[];
  labels: { what: string; why: string; next: string; ask: string };
  className?: string;
}

const STAGE_WIDTH = 540;
/** Slightly under the full-size design, so the stack sits comfortably in the card. */
const TILE_SCALE = 0.92;

/** Tag colours by card: the first is urgent, the rest are "look this week". */
const TAG_STYLES = [
  { backgroundColor: "#fde7e7", color: "#c20a0a" },
  { backgroundColor: "#fff3d6", color: "#b4600b" },
  { backgroundColor: "#fff3d6", color: "#b4600b" },
] as const;

export function InsightVisual({
  cards,
  labels,
  className,
}: InsightVisualProps) {
  const root = React.useRef<HTMLDivElement>(null);
  const seconds = useLoopSeconds(root, INSIGHT_REST_SECONDS, 0);
  const scale = useStageScale(root, STAGE_WIDTH) * TILE_SCALE;
  const frame = getInsightFrame(seconds);

  return (
    <div
      ref={root}
      aria-hidden
      className={cn(
        "relative mt-auto h-[300px] min-[536px]:absolute min-[536px]:inset-x-6 min-[536px]:mt-0 min-[536px]:h-[calc(var(--s)*210px)] md:inset-x-10",
        className
      )}
      style={{ "--s": scale } as React.CSSProperties}
    >
      <div className="mx-auto h-full w-full min-[536px]:h-[calc(var(--s)*210px)] min-[536px]:w-[calc(var(--s)*540px)]">
        <div
          className="relative h-full w-full min-[536px]:h-[210px] min-[536px]:w-[540px] min-[536px]:origin-top-left min-[536px]:scale-[var(--s)]"
          style={{ opacity: frame.frameOpacity }}
        >
          {cards.map((card, i) => {
            const f = frame.cards[i];
            if (!f?.visible) return null;
            const tag = TAG_STYLES[i] ?? TAG_STYLES[1];
            return (
              <div
                key={card.title}
                className="border-grey-100 bg-grey-0 absolute inset-x-0 top-0 box-border origin-top rounded-xl border px-5 py-[18px] shadow-[0_2px_20px_rgba(22,21,23,0.08)] will-change-[transform,opacity]"
                style={{
                  transform: `translateY(${f.y}px) scale(${f.scale})`,
                  opacity: f.opacity,
                  zIndex: f.zIndex,
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-grey-950 min-w-0 text-[16px] leading-[1.3] font-medium tracking-[-0.02em]">
                    {card.title}
                  </span>
                  <span
                    className="shrink-0 rounded-full px-2.5 py-1 text-[12px] font-semibold whitespace-nowrap"
                    style={tag}
                  >
                    {card.tag}
                  </span>
                </div>
                <div className="text-grey-950 mt-3 grid grid-cols-[44px_1fr] gap-x-2 gap-y-1.5 text-[13px] leading-[1.45]">
                  <span
                    className="text-grey-500"
                    style={{ opacity: f.rows[0] }}
                  >
                    {labels.what}
                  </span>
                  <span style={{ opacity: f.rows[0] }}>{card.what}</span>
                  <span
                    className="text-grey-500"
                    style={{ opacity: f.rows[1] }}
                  >
                    {labels.why}
                  </span>
                  <span style={{ opacity: f.rows[1] }}>{card.why}</span>
                  <span
                    className="text-grey-500"
                    style={{ opacity: f.rows[2] }}
                  >
                    {labels.next}
                  </span>
                  <span style={{ opacity: f.rows[2] }}>{card.next}</span>
                </div>
                <div className="mt-3.5" style={{ opacity: f.rows[2] }}>
                  <span
                    className="inline-flex rounded-full p-[1.5px]"
                    style={{
                      background:
                        "linear-gradient(90deg, #ff3b6b, #7b3bff, #001eff)",
                    }}
                  >
                    <span className="bg-grey-0 text-grey-950 rounded-full px-3.5 py-1.5 text-[13px] font-medium">
                      {labels.ask}
                    </span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
